import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import {
  PhoneIcon,
  EnvelopeIcon as MailIcon,
  MapPinIcon as LocationMarkerIcon,
} from '@heroicons/react/24/outline';
import {
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
} from 'react-icons/fa';
import { LanguageContext } from '../../App';

interface SocialLinkProps {
  href: string;
  icon: React.ReactNode;
}

const translations = {
  en: {
    title: 'Radhe Krishna Sewa Samiti',
    description: 'A non-governmental organization dedicated to helping underprivileged sections of society.',
    quickLinks: 'Quick Links',
    about: 'About Us',
    services: 'Services',
    volunteer: 'Become a Volunteer',
    contact: 'Contact Us',
    contactInfo: 'Contact Info',
    address: 'Village – Chheet Ka Purwa, Post – Gauri, District – Fatehpur (Uttar Pradesh), India',
    newsletter: 'Newsletter',
    newsletterDesc: 'Subscribe to stay updated with our activities',
    emailPlaceholder: 'Your Email',
    subscribe: 'Subscribe',
    copyright: '© 2024 Radhe Krishna Sewa Samiti. All rights reserved.'
  },
  hi: {
    title: 'राधे कृष्णा सेवा समिति',
    description: 'समाज के वंचित वर्गों की मदद के लिए समर्पित एक गैर-सरकारी संगठन।',
    quickLinks: 'त्वरित लिंक',
    about: 'हमारे बारे में',
    services: 'सेवाएं',
    volunteer: 'स्वयंसेवक बनें',
    contact: 'संपर्क करें',
    contactInfo: 'संपर्क करें',
    address: 'ग्राम – छीट का पुरवा, पोस्ट – गौरी, जिला – फतेहपुर (उत्तर प्रदेश), भारत',
    newsletter: 'न्यूज़लेटर',
    newsletterDesc: 'हमारी गतिविधियों की जानकारी के लिए सब्सक्राइब करें',
    emailPlaceholder: 'आपका ईमेल',
    subscribe: 'सब्सक्राइब करें',
    copyright: '© 2024 राधे कृष्णा सेवा समिति। सर्वाधिकार सुरक्षित।'
  }
};

const SocialLink: React.FC<SocialLinkProps> = ({ href, icon }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-gray-400 hover:text-white transition-colors"
  >
    {icon}
  </a>
);

const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const { language } = useContext(LanguageContext);
  const t = translations[language];

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle newsletter subscription
    console.log('Subscribed:', email);
    setEmail('');
  };

  const socialLinks = [
    { href: 'https://facebook.com', icon: FaFacebook({ size: 24 }) },
    { href: 'https://twitter.com', icon: FaTwitter({ size: 24 }) },
    { href: 'https://instagram.com', icon: FaInstagram({ size: 24 }) },
    { href: 'https://linkedin.com', icon: FaLinkedin({ size: 24 }) },
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About Section */}
          <div>
            <h3 className="text-xl font-baloo font-bold mb-4">{t.title}</h3>
            <p className="text-gray-400 font-hindi">
              {t.description}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-baloo font-bold mb-4">{t.quickLinks}</h3>
            <ul className="space-y-2 font-hindi">
              <li>
                <Link to="/about" className="text-gray-400 hover:text-white transition-colors">
                  {t.about}
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-gray-400 hover:text-white transition-colors">
                  {t.services}
                </Link>
              </li>
              <li>
                <Link to="/volunteer" className="text-gray-400 hover:text-white transition-colors">
                  {t.volunteer}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">
                  {t.contact}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-baloo font-bold mb-4">{t.contactInfo}</h3>
            <ul className="space-y-3 font-hindi">
              <li className="flex items-center space-x-3">
                <PhoneIcon className="h-5 w-5 text-primary flex-shrink-0" />
                <span className="text-gray-400">+91 95409 36061</span>
              </li>
              <li className="flex items-center space-x-3">
                <MailIcon className="h-5 w-5 text-primary flex-shrink-0" />
                <span className="text-gray-400">rksstrust00@gmail.com</span>
              </li>
              <li className="flex items-start space-x-3">
                <LocationMarkerIcon className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                <span className="text-gray-400">{t.address}</span>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-xl font-baloo font-bold mb-4">{t.newsletter}</h3>
            <p className="text-gray-400 font-hindi mb-4">
              {t.newsletterDesc}
            </p>
            <form onSubmit={handleSubscribe} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.emailPlaceholder}
                className="w-full px-4 py-2 rounded-lg bg-gray-800 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="submit"
                className="w-full btn-primary"
              >
                {t.subscribe}
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 font-hindi text-center md:text-left">
              {t.copyright}
            </p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              {socialLinks.map(({ href, icon }) => (
                <SocialLink key={href} href={href} icon={icon} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 